import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from './index.module.css';
import DateFilter, { DateFields } from '../../../components/common/table/date-filter/date-filter';
import { ButtonComponent as Button } from '../../../components/form/elements/button';
import InfoModal from '../../../components/modals/info-modal';
import { useService } from '../../../services/services';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import Spinner from '../../../components/common/spinner';
import SummaryGraph from './summary-graph';
import Table from '../../../components/common/table/table';
import LivestockSummary, { ControlDates, CowChartEntry, InseminationChartEntry, LactationChartEntry, LatestDateResponse, SomaticCellsChartEntry } from '../../../interfaces/livestock-summary';
import { route } from '../../../services/utils';
import LivestockMetabolism from '../../../interfaces/livestock-metabolism';
import LivestockSomaticCells from '../../../interfaces/livestock-somatic-cells';
import LivestockReproduction from '../../../interfaces/livestock-reproduction';
import {
  LIVESTOCK_METABOLISM_VIEW,
  LIVESTOCK_SUMMARY_VIEW,
  LIVESTOCK_SOMATIC_CELLS_VIEW,
  LIVESTOCK_REPRODUCTION_VIEW,
} from '../../../router/routes';
import { ListParams } from '../../../services/http-client';
import { map } from 'rxjs/operators';
import { PollingStatus } from '../../../enums/polling-status';

export default function MilkProduction() {
  const { t } = useTranslation();
  const dateFilterName = 'livestock-filter';
  const [somaticCellsData, setSomaticCellsData] = useState<SomaticCellsChartEntry>({
    averageCellsCount: [],
    goal: 0,
  });
  const [lactationData, setLactationData] = useState<LactationChartEntry[]>([]);
  const [cowData, setCowData] = useState<CowChartEntry[]>([]);
  const [inseminationData, setInseminationData] = useState<InseminationChartEntry>({
    insemination: [],
    goal: 0,
  });
  const [controlDates, setControlDates] = useState<ControlDates>({
    current_date: '',
    prev_date: '',
    prev_year: '',
  });
  const searchParams = new URLSearchParams(window.location.search).get('date');
  const { summaryTaskService, livestockSummaryService } = useService();
  const { farmId } = useParams<{ farmId: string }>();
  const history = useHistory();
  const files = React.useRef(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [modal, setModal] = useState(null);
  const [latestDate, setLatestDate] = useState<string | null>(null);
  const [expandedChart, setExpandedChart] = useState(null);
  const pollTimeLimit = 30 * 60 * 1000;
  const pollInterval = 3000;

  const handleChartClick = (chartId: number) => {
    setExpandedChart(expandedChart === chartId ? null : chartId);
  };

  type DataObject = {
    dataKey: string;
    dataTitle?: string;
    strokeColor: string;
    yAxis: 'left' | 'right' | 'third';
  };

  type Chart = {
    id: number;
    title: string;
    xKey: string;
    data: Array<any>;
    goal?: number;
    dataKeyScatter?: string;
    dataTitleScatter?: string;
    dataObjects?: DataObject[];
    barObjects?: DataObject[];
  };

  const charts: Chart[] = [
    {
      id: 1,
      title: t('pages.livestock.summary.section.average_somatic_cell_count'),
      xKey: "current_date",
      dataKeyScatter: "cow",
      dataTitleScatter: t('pages.livestock.summary.view.low_yield_cow'),
      dataObjects: [
        {
          dataKey: "somaticCells",
          dataTitle: t('pages.livestock.summary.section.scc'),
          strokeColor: "#12589F",
          yAxis: "left"
        },
      ] as DataObject[],
      data: somaticCellsData.averageCellsCount,
      goal: somaticCellsData.goal,
    },
    {
      id: 2,
      title: t('pages.livestock.summary.section.herd_summary'),
      xKey: "current_date",
      dataObjects: [
        {
          dataKey: "cows",
          dataTitle: t('pages.livestock.summary.section.lactating_cow'),
          strokeColor: "#12589F",
          yAxis: "right"
        },
        {
          dataKey: "days",
          dataTitle: t('pages.livestock.summary.section.days_in_milk'),
          strokeColor: "#50AE2F",
          yAxis: "left"
        },
        {
          dataKey: "produce",
          dataTitle: t('pages.livestock.summary.section.cow_yield_produce'),
          strokeColor: "#E791A9",
          yAxis: "right"
        },
      ] as DataObject[],
      data: cowData,
    },
    {
      id: 3,
      title: t('pages.livestock.summary.section.starvation_based_on_days_milked'),
      xKey: "interval",
      dataObjects: [
        {
          dataKey: "milkCurrent",
          dataTitle: t('pages.livestock.summary.view.this_control'),
          strokeColor: "#50AE2F",
          yAxis: "left"
        },
        {
          dataKey: "milkPrev",
          dataTitle: t('pages.livestock.summary.view.prev_control'),
          strokeColor: "#12589F",
          yAxis: "left"
        },
      ] as DataObject[],
      data: lactationData,
    },
    {
      id: 4,
      title: t('pages.livestock.summary.section.first_insemination_after_calving'),
      xKey: "interval",
      barObjects: [
        {
          dataKey: "value",
          dataTitle: t('pages.livestock.summary.view.optimal_insemination_period'),
          strokeColor: "#12589F",
          yAxis: "left"
        },
      ] as DataObject[],
      data: inseminationData.insemination,
      goal: inseminationData.goal,
    },
  ];

  const requestSummary = useCallback(
    (page: number, sort: string, desc: boolean, filter: ListParams) => {
      return livestockSummaryService.index(farmId, 'summary', {
        page,
        sort,
        desc,
        ...filter,
      });
    },
    [livestockSummaryService, farmId]
  );

  const requestMetabolism = useCallback(
    (page: number, sort: string, desc: boolean, filter: ListParams) => {
      return livestockSummaryService.index(farmId, 'metabolism', {
        page,
        sort,
        desc,
        ...filter,
      });
    },
    [livestockSummaryService, farmId]
  );

  const requestSomaticCells = useCallback(
    (page: number, sort: string, desc: boolean, filter: ListParams) => {
      return livestockSummaryService.index(farmId, 'somatic_cells', {
        page,
        sort,
        desc,
        ...filter,
      });
    },
    [livestockSummaryService, farmId]
  );

  const requestReproduction = useCallback(
    (page: number, sort: string, desc: boolean, filter: ListParams) => {
      return livestockSummaryService.index(farmId, 'reproduction', {
        page,
        sort,
        desc,
        ...filter,
      });
    },
    [livestockSummaryService, farmId]
  );

  useEffect(() => {
    const somaticCellsSubscription = livestockSummaryService.somaticCellsChart(farmId, searchParams).subscribe(
      (response: SomaticCellsChartEntry) => {
        setSomaticCellsData(response);
      }
    );

    const lactationSubscription = livestockSummaryService.lactationChart(farmId, searchParams).subscribe(
      (response: LactationChartEntry[]) => {
        setLactationData(response);
      }
    );

    const cowSubscription = livestockSummaryService.cowChart(farmId, searchParams).subscribe(
      (response: CowChartEntry[]) => {
        setCowData(response);
      }
    );

    const inseminationSubscription = livestockSummaryService.inseminationChart(farmId, searchParams).subscribe(
      (response: InseminationChartEntry) => {
        setInseminationData(response);
      }
    );

    const controlDatesSubscription = livestockSummaryService.displayControlDates(farmId, searchParams).subscribe(
      (response: ControlDates) => {
        setControlDates(response);
      }
    );

    return () => {
      somaticCellsSubscription.unsubscribe();
      lactationSubscription.unsubscribe();
      cowSubscription.unsubscribe();
      inseminationSubscription.unsubscribe();
      controlDatesSubscription.unsubscribe();
    };
  }, [livestockSummaryService, farmId, searchParams, loading]);

  useEffect(() => {
    const search = new URLSearchParams(history.location.search);
    const selectedDate = search.get(DateFields.date);
  
    const subscription = livestockSummaryService.getLatestDate(farmId).subscribe((response: LatestDateResponse) => {
      setLatestDate(selectedDate || response.latestDate);
    });
  
    return () => subscription.unsubscribe();
  }, [livestockSummaryService, farmId, history]);
  
  useEffect(() => {
    if (!loading && latestDate) {
      const formattedDate = moment(latestDate).format('YYYY-MM-DD');

      const search = new URLSearchParams(history.location.search);
      search.set(DateFields.date, formattedDate);
      history.replace({ ...history.location, search: search.toString() });
    }
  }, [history, latestDate, loading]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = Array.from(e.target.files || []);
    files.current = selectedFile;

    const fileNameElement = document.getElementById('fileName');

    if (fileNameElement) {
      if (selectedFile.length > 0) {
        fileNameElement.textContent = selectedFile.map((file) => (file as File).name).join(', ');
      } else {
        fileNameElement.textContent = t('pages.livestock.summary.section.no_files_selected');
      }
    }
  };

  const pollStatus = async (farmId: string, showModal: (status: string) => void) => {
    try {
      let isPolling = true;
      const startTime = Date.now();

      while (isPolling) {
          if (Date.now() - startTime > pollTimeLimit) {
            isPolling = false;
            showModal(PollingStatus.COMPLETED);
            return;
        }

        const statusResponse = await livestockSummaryService.getMessageStatus(farmId)
        .pipe(
          map((response) => (typeof response === 'string' ? JSON.parse(response) : response))
        )
        .toPromise();
  
        if (statusResponse.status === PollingStatus.COMPLETED || statusResponse.status === PollingStatus.FAILED) {
          isPolling = false;
  
          showModal(statusResponse.status);
        }
  
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }
    } catch (error) {
      setModal(
        <InfoModal
          title={t('pages.livestock.summary.section.import_from_csv')}
          content={t('pages.livestock.summary.section.import_failed')}
          buttonText={t('pages.livestock.summary.section.continue')}
          onClose={() => setModal(null)}
        />
      );
    }
  };

  const handleTaskAndShowModal = async (farmId: string, formData: FormData) => {
    try {
      await summaryTaskService.store(farmId, formData).toPromise();

      setLoading(true);

      pollStatus(farmId, (status: string) => {
        setLoading(false);

        livestockSummaryService.getLatestDate(farmId).subscribe((response: LatestDateResponse) => {
          setLatestDate(response.latestDate);
        });
  
        if (status === PollingStatus.COMPLETED) {
          setModal(
            <InfoModal
              title={t('pages.livestock.summary.section.import_from_csv')}
              content={t('pages.livestock.summary.section.analysis_complete')}
              buttonText={t('pages.livestock.summary.section.continue')}
              onClose={() => {
                setModal(null);
                window.location.reload();
              }}
            />
          );
        } else if (status === PollingStatus.FAILED) {
          setModal(
            <InfoModal
              title={t('pages.livestock.summary.section.import_from_csv')}
              content={t('pages.livestock.summary.section.import_failed')}
              buttonText={t('pages.livestock.summary.section.continue')}
              onClose={() => setModal(null)}
            />
          );
        }
      });
    } catch (error) {
      setLoading(false);
      setModal(
        <InfoModal
          title={t('pages.livestock.summary.section.import_from_csv')}
          content={t('pages.livestock.summary.section.import_failed')}
          buttonText={t('pages.livestock.summary.section.continue')}
          onClose={() => setModal(null)}
        />
      );
    }
  };
  
  const handleOnSubmit = () => {
    if (files.current) {
      const requiredFiles = ['ATN.csv', 'KON.csv', 'SEK.csv'];
      const uploadedFileNames = files.current.map((file) => file.name);

      const allFilesPresent = requiredFiles.every(file => uploadedFileNames.includes(file));

      if (!allFilesPresent) {
        setModal(
          <InfoModal
            title={t('pages.livestock.summary.section.import_from_csv')}
            content={t('pages.livestock.summary.section.missing_files')}
            buttonText={t('pages.livestock.summary.section.continue')}
            onClose={() => {
              setModal(null);
            }}
          />
        );
        return;
      }

      setLoading(true);
      const formData = new FormData();
      for (let i = 0; i < files.current.length; i++) {
        formData.append('array[]', files.current[i]);
      }

      handleTaskAndShowModal(farmId, formData)
    }
  }

  if (loading) {
    return (
      <div className={css['loading']}>
        <div>{t('Loading')}</div>
        <Spinner visible={true} size='lg' />
      </div>
    );
  }

  return (
    <div>
      <div className={css['top-bar']}>
        <div>
          <span>
            {t('pages.livestock.summary.section.control_date')}
          </span>
          <DateFilter
            name={dateFilterName}
            fields={[DateFields.date]}
            showDateControls={true}
          />
        </div>
        <Button
          name="import_csv_button"
          label={t('pages.livestock.summary.section.import_from_csv')}
          click={() =>
            setModal(
              <InfoModal
                title={t(
                  'pages.livestock.summary.section.import_from_csv_desc'
                )}
                content={
                  <div>
                    <input
                      id="fileInput"
                      type="file"
                      accept=".csv"
                      onChange={handleOnChange}
                      multiple
                      style={{ display: 'none' }}
                    />
                    <label htmlFor='fileInput' className={css['upload-btn']}>
                      {t('pages.livestock.summary.section.browse_button')}
                    </label>
                    <span id="fileName">{t('pages.livestock.summary.section.no_files_selected')}</span>
                  </div>
                }
                buttonText={'pages.livestock.summary.section.import'}
                onClose={() => {
                  setModal(null);
                  handleOnSubmit();
                }}
              />
            )
          }
        />
      </div>

      <div className={css['grid-container']}>
        {charts.map((chart) => {
          if (expandedChart === null || expandedChart === chart.id) {
            return (
              <div
                key={chart.id}
                className={`${css['chart-container']} ${expandedChart === chart.id ? css['expanded'] : ''}`}
                onClick={() => handleChartClick(chart.id)}
              >
                <SummaryGraph
                  title={chart.title}
                  height={expandedChart === chart.id ? 350 : 193}
                  xKey={chart.xKey}
                  dataKeyScatter={chart.dataKeyScatter ? chart.dataKeyScatter : ''}
                  dataTitleScatter={chart.dataKeyScatter ? chart.dataTitleScatter : ''}
                  dataObjects={chart.dataObjects}
                  barObjects={chart.barObjects}
                  data={chart.data}
                  isExpandable={true}
                  goal={chart.goal ? chart.goal : null}
                  yAxisGoal={chart.id !== 4 ? chart.goal ? 'left' : '' : 'third'}
                  xTitle=''
                />
              </div>
            );
          }
          return null;
        })}
      </div>

      <div className={css['grid-container']}>
        <div>
          <h4 className={css['table-title']}>
            {t('pages.livestock.summary.section.summary').toUpperCase()}
          </h4>
          <Table<LivestockSummary>
            request={requestSummary}
            keyAccessor="id"
            paginate={false}
            columnGroups={[
              {
                header: t(
                  'pages.livestock.summary.section.last_control_date'
                ),
                startsAtAccessor: 'last_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_control_date'
                ),
                startsAtAccessor: 'prev_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_year'
                ),
                startsAtAccessor: 'prev_year_control',
                colspan: 1,
              },
            ]}
            columns={[
              {
                accessor: 'last_control',
                header: controlDates.current_date,
              },
              {
                accessor: 'prev_control',
                header: controlDates.prev_date,
              },
              {
                accessor: 'prev_year_control',
                header: controlDates.prev_year,
              },
              {
                accessor: 'measurement',
                header: t('pages.livestock.summary.section.metric'),
              },
              {
                accessor: 'summary',
                header: t('pages.livestock.summary.section.title'),
                className: css['strong'],
              },
            ]}
            viewRoute={route(LIVESTOCK_SUMMARY_VIEW, {
              farmId: farmId,
            })}
            viewRecordAbility={{ title: 'view livestock_summary' }}
          />
        </div>

        <div>
          <h4 className={css['table-title']}>
            {t(
              'pages.livestock.summary.section.metabolism'
            ).toUpperCase()}
          </h4>
          <Table<LivestockMetabolism>
            request={requestMetabolism}
            keyAccessor="id"
            paginate={false}
            columnGroups={[
              {
                header: t(
                  'pages.livestock.summary.section.last_control_date'
                ),
                startsAtAccessor: 'last_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_control_date'
                ),
                startsAtAccessor: 'prev_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_year'
                ),
                startsAtAccessor: 'prev_year_control',
                colspan: 1,
              },
            ]}
            columns={[
              {
                accessor: 'last_control',
                header: controlDates.current_date,
                className: css['wide-column'],
              },
              {
                accessor: 'prev_control',
                header: controlDates.prev_date,
                className: css['wide-column'],
              },
              {
                accessor: 'prev_year_control',
                header: controlDates.prev_year,
                className: css['small-column'],
              },
              {
                accessor: 'measurement',
                header: t('pages.livestock.summary.section.metric'),
              },
              {
                accessor: 'summary',
                header: t('pages.livestock.summary.section.title'),
                className: css['strong'],
              },
            ]}
            viewRoute={route(LIVESTOCK_METABOLISM_VIEW, {
              farmId: farmId,
            })}
            viewRecordAbility={{ title: 'view livestock_metabolism' }}
          />
        </div>

        <div>
          <h4 className={css['table-title']}>
            {t(
              'pages.livestock.summary.section.somatic_cell_count_or_udder_health'
            ).toUpperCase()}
          </h4>
          <Table<LivestockSomaticCells>
            request={requestSomaticCells}
            keyAccessor="id"
            paginate={false}
            columnGroups={[
              {
                header: t(
                  'pages.livestock.summary.section.last_control_date'
                ),
                startsAtAccessor: 'last_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_control_date'
                ),
                startsAtAccessor: 'prev_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_year'
                ),
                startsAtAccessor: 'prev_year_control',
                colspan: 1,
              },
            ]}
            columns={[
              {
                accessor: 'last_control',
                header: controlDates.current_date,
                className: css['wide-column'],
              },
              {
                accessor: 'prev_control',
                header: controlDates.prev_date,
                className: css['wide-column'],
              },
              {
                accessor: 'prev_year_control',
                header: controlDates.prev_year,
                className: css['small-column'],
              },
              {
                accessor: 'measurement',
                header: t('pages.livestock.summary.section.metric'),
              },
              {
                accessor: 'summary',
                header: t('pages.livestock.summary.section.title'),
                className: css['strong'],
              },
            ]}
            viewRoute={route(LIVESTOCK_SOMATIC_CELLS_VIEW, {
              farmId: farmId,
            })}
            viewRecordAbility={{ title: 'view livestock_somatic_cell' }}
          />
        </div>

        <div>
          <h4 className={css['table-title']}>
            {t(
              'pages.livestock.summary.section.reproduction'
            ).toUpperCase()}
          </h4>
          <Table<LivestockReproduction>
            request={requestReproduction}
            keyAccessor="id"
            paginate={false}
            columnGroups={[
              {
                header: t(
                  'pages.livestock.summary.section.last_control_date'
                ),
                startsAtAccessor: 'last_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_control_date'
                ),
                startsAtAccessor: 'prev_control',
                colspan: 1,
              },
              {
                header: t(
                  'pages.livestock.summary.section.previous_year'
                ),
                startsAtAccessor: 'prev_year_control',
                colspan: 1,
              },
            ]}
            columns={[
              {
                accessor: 'last_control',
                header: controlDates.current_date,
                className: css['wide-column'],
              },
              {
                accessor: 'prev_control',
                header: controlDates.prev_date,
                className: css['wide-column'],
              },
              {
                accessor: 'prev_year_control',
                header: controlDates.prev_year,
                className: css['small-column'],
              },
              {
                accessor: 'measurement',
                header: t('pages.livestock.summary.section.metric'),
              },
              {
                accessor: 'summary',
                header: t('pages.livestock.summary.section.title'),
                className: css['strong'],
              },
            ]}
            viewRoute={route(LIVESTOCK_REPRODUCTION_VIEW, {
              farmId: farmId,
            })}
            viewRecordAbility={{ title: 'view livestock_reproduction' }}
          />
        </div>
      </div>
      {modal}
    </div>
  );
}
